import { FC, useState } from "react";
import { useDetailBookingStore } from "../../detail-booking.store";
import { renderChannel } from "~/utils/channels";
import { PreferredCommunicationMethod } from "~/utils/constants";
import dayjs from "dayjs";
import { Button, Space, Table, TableColumnsType, Tag, Tooltip, Modal } from "antd";
import { TCommunication, RecipientType } from "~/types";
import { EyeOutlined, MailOutlined, TeamOutlined, ArrowRightOutlined, SendOutlined, UserOutlined } from "@ant-design/icons";
import { ReplyModal } from "./reply-modal";

type Props = {};

export const TableCommunication: FC<Props> = () => {
  const { booking } = useDetailBookingStore();
  const [viewModal, setViewModal] = useState<TCommunication | null>(null);
  const [replyModal, setReplyModal] = useState<TCommunication | null>(null);

  // Sort communications by date (most recent first)
  const sortedCommunications = booking?.communications?.slice().sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  }) || [];

  // Helper to clean HTML for display (remove tracking comment)
  const cleanHtmlForDisplay = (html: string | null | undefined): string => {
    if (!html) return '';
    // Remove tracking HTML comment
    return html.replace(/<!--\s*AHC-TRACKING:[^>]*-->/gi, '').trim();
  };

  const getRecipientTypeLabel = (type: RecipientType) => {
    const labels: Record<RecipientType, string> = {
      [RecipientType.ASSIGNED_STAFF]: 'Assigned Staff',
      [RecipientType.ALL_ADMINS]: 'All Admins',
      [RecipientType.ALL_STAFF]: 'All Staff',
      [RecipientType.BOOKING_CLIENT]: 'Booking Client',
      [RecipientType.SERVICE_CLIENTS]: 'Service Clients',
      [RecipientType.SPECIFIC_USERS]: 'Specific Users',
      [RecipientType.SPECIFIC_STAFF]: 'Specific Staff',
      [RecipientType.SPECIFIC_CLIENTS]: 'Specific Clients',
      [RecipientType.MENTIONED_USERS]: 'Mentioned Users',
    };
    return labels[type] || type;
  };

  const columns: TableColumnsType<TCommunication> = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (text) => dayjs(text).format("MMM D, YYYY hh:mm A"),
    },
    {
      title: "Type",
      dataIndex: "channels",
      key: "channels",
      width: 80,
      render: (text: PreferredCommunicationMethod) => {
        const channelLabel = renderChannel[text] || text;
        return (
          <Tag color="blue">
            {channelLabel}
          </Tag>
        );
      },
    },
    {
      title: "Sent By",
      key: "sentBy",
      width: 150,
      render: (_, record) => {
        if (record.isReply) {
          return (
            <div className="flex items-center gap-1 text-blue-600">
              <ArrowRightOutlined className="text-xs" />
              <span className="text-sm">{record.replyFromName || record.replyFromEmail || 'External'}</span>
            </div>
          );
        }

        if (record.sentBy) {
          const fullName = `${record.sentBy.firstName} ${record.sentBy.lastName}`.trim();
          return (
            <Tooltip title={record.sentBy.email}>
              <div className="flex items-center gap-1">
                <UserOutlined className="text-gray-400" />
                <div>
                  <div className="text-sm font-medium">{fullName}</div>
                  {record.sentBy.username && (
                    <div className="text-xs text-gray-500">@{record.sentBy.username}</div>
                  )}
                </div>
              </div>
            </Tooltip>
          );
        }

        return <span className="text-gray-400 italic text-sm">Unknown</span>;
      },
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      width: 400,
      ellipsis: true,
      render: (text, record) => (
        <div className="flex flex-col">
          {record.isReply && (
            <div className="flex items-center gap-1 mb-1">
              <ArrowRightOutlined className="text-blue-500 text-xs" />
              <Tag color="blue" size="small">Reply from {record.replyFromName || record.replyFromEmail}</Tag>
            </div>
          )}
          <span>{text || <span className="text-gray-400 italic">No subject</span>}</span>
        </div>
      ),
    },
    {
      title: "Recipients",
      key: "recipients",
      width: 200,
      render: (_, record) => {
        const recipientTypes = record.recipientTypes || [];
        const recipientEmails = record.recipientEmails || [];
        const totalCount = record.sentToCount || recipientEmails.length;

        if (recipientTypes.length === 0 && recipientEmails.length === 0) {
          // Fallback to old behavior
          return (
            <div className="flex items-center space-x-1">
              <MailOutlined className="text-gray-400" />
              <span>{booking?.client?.email || 'N/A'}</span>
            </div>
          );
        }

        return (
          <div className="flex flex-col space-y-1">
            {recipientTypes.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {recipientTypes.slice(0, 2).map(type => (
                  <Tag key={type} size="small" icon={<TeamOutlined />}>
                    {getRecipientTypeLabel(type)}
                  </Tag>
                ))}
                {recipientTypes.length > 2 && (
                  <Tag size="small">+{recipientTypes.length - 2} more</Tag>
                )}
              </div>
            )}
            <div className="text-xs text-gray-500">
              {totalCount > 0 && (
                <span>{totalCount} recipient{totalCount !== 1 ? 's' : ''}</span>
              )}
              {recipientEmails.length > 0 && recipientEmails.length <= 3 && (
                <div className="mt-1">
                  {recipientEmails.map((email, idx) => (
                    <div key={idx} className="text-gray-600">{email}</div>
                  ))}
                </div>
              )}
              {recipientEmails.length > 3 && (
                <Tooltip title={recipientEmails.join(', ')}>
                  <span className="cursor-help underline">
                    {recipientEmails.slice(0, 2).join(', ')} +{recipientEmails.length - 2} more
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) => {
        const isSent = !record.isReply;
        return (
          <Tag color={isSent ? "blue" : "green"}>
            {isSent ? "Sent" : "Received"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              className="text-gray-400 hover:text-blue-600"
              onClick={() => setViewModal(record)}
            />
          </Tooltip>
          {record.channels === PreferredCommunicationMethod.EMAIL && (
            <Tooltip title="Reply">
              <Button
                type="text"
                icon={<SendOutlined />}
                className="text-gray-400 hover:text-green-600"
                onClick={() => setReplyModal(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="border rounded-lg overflow-hidden mb-6">
        <Table
          columns={columns}
          dataSource={sortedCommunications}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showTotal: (total) => `Total ${total} communications`,
          }}
          className="w-full"
          rowClassName={(record) =>
            record.isReply
              ? "hover:bg-blue-50 bg-blue-50/30 border-l-2 border-l-blue-400"
              : "hover:bg-gray-50"
          }
          locale={{
            emptyText: 'No communications yet. Send your first message above!',
          }}
        />
      </div>

      {/* View Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <MailOutlined />
            <span>Communication Details</span>
          </div>
        }
        open={!!viewModal}
        onCancel={() => setViewModal(null)}
        footer={[
          <Button key="close" onClick={() => setViewModal(null)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {viewModal && (
          <div className="space-y-4">
            {viewModal.isReply && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowRightOutlined className="text-blue-500" />
                  <span className="font-semibold text-blue-900">Reply from recipient</span>
                </div>
                <div className="text-sm space-y-1">
                  <div><strong>From:</strong> {viewModal.replyFromName || viewModal.replyFromEmail}</div>
                  {viewModal.replyFromEmail && (
                    <div><strong>Email:</strong> {viewModal.replyFromEmail}</div>
                  )}
                  {viewModal.parentCommunication && (
                    <div><strong>In reply to:</strong> {viewModal.parentCommunication.communicationRef}</div>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Reference</label>
              <div className="text-gray-900">{viewModal.communicationRef}</div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Date</label>
              <div className="text-gray-900">
                {dayjs(viewModal.createdAt).format("MMMM D, YYYY [at] h:mm A")}
              </div>
            </div>

            {!viewModal.isReply && viewModal.sentBy && (
              <div>
                <label className="text-sm font-medium text-gray-700">Sent By</label>
                <div className="text-gray-900 flex items-center gap-2 mt-1">
                  <UserOutlined className="text-gray-400" />
                  <span>
                    {viewModal.sentBy.firstName} {viewModal.sentBy.lastName}
                    {viewModal.sentBy.username && (
                      <span className="text-gray-500 ml-1">(@{viewModal.sentBy.username})</span>
                    )}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">{viewModal.sentBy.email}</span>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <div className="text-gray-900">{viewModal.subject || 'No subject'}</div>
            </div>

            {!viewModal.isReply && (
              <div>
                <label className="text-sm font-medium text-gray-700">Recipients</label>
                <div className="mt-2 space-y-2">
                  {viewModal.recipientTypes && viewModal.recipientTypes.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">Recipient Groups:</div>
                      <div className="flex flex-wrap gap-1">
                        {viewModal.recipientTypes.map(type => (
                          <Tag key={type} icon={<TeamOutlined />}>
                            {getRecipientTypeLabel(type)}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                  {viewModal.recipientEmails && viewModal.recipientEmails.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        Email Addresses ({viewModal.recipientEmails.length}):
                      </div>
                      <div className="bg-gray-50 p-2 rounded max-h-40 overflow-y-auto">
                        {viewModal.recipientEmails.map((email, idx) => (
                          <div key={idx} className="text-sm text-gray-700 flex items-center space-x-2">
                            <MailOutlined className="text-gray-400" />
                            <span>{email}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {viewModal.sentToCount && (
                    <div className="text-sm text-gray-600">
                      Total: {viewModal.sentToCount} recipient{viewModal.sentToCount !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Message</label>
              <div className="mt-1 p-3 bg-white rounded border max-h-96 overflow-y-auto">
                {viewModal.htmlBody ? (
                  <div
                    className="email-content"
                    dangerouslySetInnerHTML={{ __html: cleanHtmlForDisplay(viewModal.htmlBody) }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap">{viewModal.body || 'No message body'}</div>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <div>
                <Tag color="green" className="capitalize">
                  {viewModal.status ?? "delivered"}
                </Tag>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Reply Modal */}
      <ReplyModal
        communication={replyModal}
        onClose={() => setReplyModal(null)}
      />
    </>
  );
};
